import { useCallback, useState } from 'react';

import {
  ALLOWED_FEEDBACK_IMAGE_MIME_TYPES,
  MAX_FEEDBACK_IMAGE_ATTACHMENTS,
} from './feedback.constants';
import type { FeedbackAttachmentItem } from './feedbackAttachment.types';
import {
  isFeedbackImageWithinMaxBytes,
  normalizeSelectedFiles,
} from './utils/feedbackAttachment.utils';
import { readFileAsDataUrl } from './utils/readFileAsDataUrl';

/**
 * Local attachment previews and validation for {@link FeedbackModal}.
 * Previews use `data:` URLs so they comply with strict `img-src` CSP (no `blob:`).
 */
export function useFeedbackModalAttachments() {
  const [attachmentItems, setAttachmentItems] = useState<
    FeedbackAttachmentItem[]
  >([]);
  const [uploadSizeError, setUploadSizeError] = useState(false);

  const clearAttachments = useCallback(() => {
    setAttachmentItems([]);
    setUploadSizeError(false);
  }, []);

  const handleSizeError = useCallback(() => {
    setUploadSizeError(true);
  }, []);

  const handleAttachmentFilesSelected = useCallback(
    (selected: File | FileList | File[] | null) => {
      const incoming = normalizeSelectedFiles(selected).filter((file) =>
        ALLOWED_FEEDBACK_IMAGE_MIME_TYPES.has(file.type)
      );

      if (incoming.length === 0) return;

      const oversized = incoming.filter(
        (file) => !isFeedbackImageWithinMaxBytes(file)
      );
      const valid = incoming.filter(isFeedbackImageWithinMaxBytes);

      if (oversized.length > 0) {
        setUploadSizeError(true);
      } else if (valid.length > 0) {
        setUploadSizeError(false);
      }

      if (valid.length === 0) return;

      void (async () => {
        try {
          const withPreviews: FeedbackAttachmentItem[] = await Promise.all(
            valid.map(async (file) => ({
              id: crypto.randomUUID(),
              file,
              previewUrl: await readFileAsDataUrl(file),
            }))
          );

          setAttachmentItems((prev) => {
            const remaining = MAX_FEEDBACK_IMAGE_ATTACHMENTS - prev.length;
            if (remaining <= 0) return prev;

            const toAdd = withPreviews.slice(0, remaining);
            return [...prev, ...toAdd];
          });
        } catch {
          setUploadSizeError(true);
        }
      })();
    },
    []
  );

  const handleRemoveAttachment = useCallback((id: string) => {
    setAttachmentItems((prev) => prev.filter((item) => item.id !== id));
  }, []);

  return {
    attachmentItems,
    uploadSizeError,
    handleAttachmentFilesSelected,
    handleAttachmentSizeError: handleSizeError,
    handleRemoveAttachment,
    clearAttachments,
  };
}
